import { useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import {
  createEvent,
  deleteEvent,
  getEventById,
  listEvents,
  listPromotableEvents,
  updateEvent
} from '../services/eventsService'

const DEFAULT_STALE_TIME = 1000 * 60 * 2
const DEFAULT_CACHE_TIME = 1000 * 60 * 10

const mapEventRecord = (event) => ({
  id: event.id,
  title: event.title ?? '',
  description: event.description ?? '',
  date: event.date ?? '',
  time: event.start_time ?? event.time ?? '',
  duration: event.duration ?? 0,
  price: event.price ?? 0,
  tieredPricing: event.tiered_pricing ?? event.tieredPricing ?? false,
  zoomLink: event.zoom_link ?? event.zoomLink ?? '',
  agentId: event.agent_id ?? event.agentId ?? null,
  status: event.status ?? 'draft',
  registrations: event.registrations ?? event.registration_count ?? 0,
  maxAttendees: event.max_attendees ?? event.maxAttendees ?? event.capacity ?? null,
  templateId: event.template_id ?? event.templateId ?? null,
  customTemplate: event.custom_template ?? event.customTemplate ?? null,
  resources: event.resources ?? [],
  recordingUrl: event.recording_url ?? event.recordingUrl ?? null,
  createdAt: event.created_at ?? event.createdAt ?? null,
  event_slug: event.event_slug ?? event.eventSlug ?? '',
  custom_domain: event.custom_domain ?? event.customDomain ?? null
})

export const eventsKeys = {
  all: ['events'],
  lists: () => [...eventsKeys.all, 'list'],
  list: (options = {}) => [...eventsKeys.lists(), options],
  details: () => [...eventsKeys.all, 'detail'],
  detail: (eventId, options = {}) => [...eventsKeys.details(), eventId, options]
}

export const promotableEventsKeys = {
  all: ['promotableEvents'],
  lists: () => [...promotableEventsKeys.all, 'list'],
  list: (options = {}) => [...promotableEventsKeys.lists(), options]
}

const subscribeToEvents = (queryClient) => {
  const channel = supabase
    .channel('events-realtime')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'events' },
      () => {
        queryClient.invalidateQueries({ queryKey: eventsKeys.all })
      }
    )
    .subscribe()

  return () => {
    supabase.removeChannel(channel)
  }
}

export const useEvents = (options = {}, config = {}) => {
  const {
    enabled = true,
    realtime = false,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!realtime) return undefined
    return subscribeToEvents(queryClient)
  }, [queryClient, realtime])

  return useQuery({
    queryKey: eventsKeys.list(options),
    queryFn: () => listEvents(options),
    enabled,
    staleTime,
    cacheTime
  })
}

/**
 * Fetch promotable events (e.g. status = 'public') for the Invitation Links page.
 * Does not filter by agent_id; RLS enforces visibility. Returns events the user
 * is allowed to promote.
 */
export const usePromotableEvents = (options = {}, config = {}) => {
  const {
    enabled = true,
    realtime = false,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!realtime) return undefined
    return subscribeToEvents(queryClient)
  }, [queryClient, realtime])

  return useQuery({
    queryKey: promotableEventsKeys.list(options),
    queryFn: async () => {
      const result = await listPromotableEvents(options)
      if (result.error) throw result.error
      return (result.data ?? []).map(mapEventRecord)
    },
    enabled,
    staleTime,
    cacheTime
  })
}

export const useEvent = (eventId, options = {}, config = {}) => {
  const {
    enabled = true,
    staleTime = DEFAULT_STALE_TIME,
    cacheTime = DEFAULT_CACHE_TIME
  } = config

  return useQuery({
    queryKey: eventsKeys.detail(eventId, options),
    queryFn: () => getEventById(eventId, options),
    enabled: enabled && Boolean(eventId),
    staleTime,
    cacheTime
  })
}

export const useCreateEvent = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload) => createEvent(payload, options),
    onSuccess: (data) => {
      queryClient.setQueryData(eventsKeys.detail(data.id, options), data)
      queryClient.invalidateQueries({ queryKey: eventsKeys.lists() })
    }
  })
}

export const useUpdateEvent = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ eventId, updates }) => updateEvent(eventId, updates, options),
    onMutate: async ({ eventId, updates }) => {
      await queryClient.cancelQueries({ queryKey: eventsKeys.all })

      const previousLists = queryClient.getQueriesData({
        queryKey: eventsKeys.lists()
      })
      const previousDetails = queryClient.getQueriesData({
        queryKey: eventsKeys.details()
      })

      previousLists.forEach(([queryKey, data]) => {
        if (!Array.isArray(data)) return
        queryClient.setQueryData(
          queryKey,
          data.map((item) =>
            item.id === eventId ? { ...item, ...updates } : item
          )
        )
      })

      previousDetails.forEach(([queryKey, data]) => {
        if (!data || data.id !== eventId) return
        queryClient.setQueryData(queryKey, { ...data, ...updates })
      })

      return { previousLists, previousDetails }
    },
    onError: (error, _variables, context) => {
      if (!context) return
      context.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: eventsKeys.detail(variables.eventId)
      })
    }
  })
}

export const useDeleteEvent = (options = {}) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (eventId) => deleteEvent(eventId, options),
    onMutate: async (eventId) => {
      await queryClient.cancelQueries({ queryKey: eventsKeys.all })

      const previousLists = queryClient.getQueriesData({
        queryKey: eventsKeys.lists()
      })
      const previousDetails = queryClient.getQueriesData({
        queryKey: eventsKeys.details()
      })

      previousLists.forEach(([queryKey, data]) => {
        if (!Array.isArray(data)) return
        queryClient.setQueryData(
          queryKey,
          data.filter((item) => item.id !== eventId)
        )
      })

      previousDetails.forEach(([queryKey, data]) => {
        if (!data || data.id !== eventId) return
        queryClient.removeQueries({ queryKey, exact: true })
      })

      return { previousLists, previousDetails }
    },
    onError: (_error, _eventId, context) => {
      if (!context) return
      context.previousLists?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
      context.previousDetails?.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data)
      })
    },
    onSettled: (_data, _error, eventId) => {
      queryClient.invalidateQueries({ queryKey: eventsKeys.lists() })
      queryClient.invalidateQueries({ queryKey: eventsKeys.detail(eventId) })
    }
  })
}
